import React from "react";
import { Modal, Table, Spin } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import styled from "styled-components";
import moment from "moment";

// ─────────────────────────────────────────────
// Reusable Modal Component
// Props:
//   open          – boolean
//   onClose       – () => void
//   loading       – boolean
//   historyData   – raw array from API
//   userList      – from Redux, to resolve updatedby email → fullname
// ─────────────────────────────────────────────
export const PromotionalHistoryModal = ({ open, onClose, loading, historyData = [], userList = [] }) => {

    const columns = [
        {
            title: "Served as",
            dataIndex: "oldlevel",
            key: "oldlevel",
            render: (text) => text || "-",
        },
        {
            title: "Served Between",
            key: "servedBetween",
            render: (_, record) => {
                const from = record.servedfrom
                    ? moment(record.servedfrom).format("Do MMM YYYY")
                    : null;
                const to = record.servedto
                    ? moment(record.servedto).format("Do MMM YYYY")
                    : "Present";
                if (!from) return "-";
                return (
                    <ServedBetweenCell>
                        <CalendarOutlined style={{ color: "#aaa", fontSize: 13 }} />
                        <span>{from} – {to}</span>
                    </ServedBetweenCell>
                );
            },
        },
        {
            title: "New Level",
            dataIndex: "newlevel",
            key: "newlevel",
            render: (text) => {
                if (!text) return "-";
                // guard: some records have a date string instead of a level name
                const looksLikeDate = /^\d{4}-\d{2}-\d{2}/.test(text);
                return looksLikeDate ? "-" : text;
            },
        },
        {
            title: "Updated by",
            key: "updatedby",
            render: (_, record) => {
                // resolve email → fullname via userList
                const user = userList?.find((u) => u.userid === record.updatedby);
                const name = user?.fullname || record.updatedby || "";
                const date = record.updatedat
                    ? moment(record.updatedat).format("Do MMM YYYY, hh:mm A")
                    : "";
                if (!name) return "-";
                return (
                    <span>
                        {name}
                        {date ? <i style={{ color: "#888", marginLeft: 4 }}>; {date}</i> : ""}
                    </span>
                );
            },
        },
    ];

    return (
        <StyledHistoryModal
            title="Promotional Level History"
            open={open}
            onCancel={onClose}
            footer={null}
            width={920}
            centered
            destroyOnClose
        >
            <Spin spinning={loading}>
                <StyledHistoryTable
                    columns={columns}
                    dataSource={(Array.isArray(historyData) ? historyData : []).map((item, idx) => ({
                        ...item,
                        key: item.id ?? idx,
                    }))}
                    pagination={false}
                    locale={{ emptyText: "No history found" }}
                />
            </Spin>
        </StyledHistoryModal>
    );
};

export const PromotionalLevelWithHistory = ({ value, onClick }) => (
    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span>{value || "-"}</span>
        <ClockIconButton
            title="View Promotional History"
            onClick={(e) => {
                e.stopPropagation();
                onClick && onClick();
            }}
        >
            <svg width="18" height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
            </svg>
        </ClockIconButton>
    </span>
);

const ClockIconButton = styled.button`
  background: #C2FAE5;
  border: 1.5px solid #C2FAE5;
  border-radius: 12%;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #000000;
  padding: 0;
  transition: background 0.18s, border-color 0.18s, color 0.18s;
`;

const ServedBetweenCell = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StyledHistoryModal = styled(Modal)`
  .ant-modal-header {
    border-bottom: 1px solid #f0f0f0;
    padding: 16px 24px;
  }
  .ant-modal-title {
    font-size: 16px;
    font-weight: 600;
    color: #1f243b;
  }
  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
  }
  .ant-modal-body {
    padding: 20px 24px 24px;
  }
`;

const StyledHistoryTable = styled(Table)`
  .ant-table-content {
    border: 1px solid #e8e8ef;
    border-radius: 8px;
    overflow: hidden;
  }
  .ant-table-thead > tr > th {
    background: #f9f9fb;
    font-size: 14px;
    font-weight: 500;
    color: #1f243b;
    border-bottom: 1px solid #e8e8ef;
  }
  .ant-table-tbody > tr > td {
    font-size: 14px;
    color: #3b3f5c;
    border-bottom: 1px solid #f4f4f7;
  }
  .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }
  .ant-table-tbody > tr:hover > td {
    background: #f5fdf9 !important;
  }
`;

export default PromotionalHistoryModal;